const EMAIL_TYPE = "email";
const EMAIL_ADDRESS_PATTERN = /^[^\s<>@]+@[^\s<>@]+$/;
const EMBEDDED_EMAIL_PATTERN = /[^\s<>(),;:\"']+@[^\s<>(),;:\"']+/g;

const getColumnLabel = (column: unknown): string => {
  if (!column || typeof column !== "object" || Array.isArray(column)) return "unknown";
  const record = column as Record<string, unknown>;
  for (const value of [record.header, record.field]) {
    if (value === undefined || value === null) continue;
    const label = String(value).trim();
    if (label) return label;
  }
  return "unknown";
};

const isEmailColumn = (column: unknown): boolean => {
  if (!column || typeof column !== "object" || Array.isArray(column)) return false;
  const record = column as Record<string, unknown>;
  return (
    String(record.type || record.columnType || "")
      .trim()
      .toLowerCase() === EMAIL_TYPE
  );
};

/**
 * Extracts one valid email address from an Email-type mapping value.
 * Empty values remain empty; invalid or ambiguous non-empty values reject the row.
 */
export const normalizeMappedEmail = (value: unknown, column: unknown): unknown => {
  if (!isEmailColumn(column) || value === undefined || value === null) return value;

  const source = String(value).trim();
  if (!source) return source;

  const angleBracketMatches = [...source.matchAll(/<\s*([^<>]+?)\s*>/g)].map((match) => match[1].trim());
  const candidates =
    angleBracketMatches.length > 0
      ? angleBracketMatches
      : (source.match(EMBEDDED_EMAIL_PATTERN) || []).map((candidate) => candidate.trim());
  const validAddresses = [...new Set(candidates.filter((candidate) => EMAIL_ADDRESS_PATTERN.test(candidate)))];

  if (validAddresses.length === 1) return validAddresses[0];

  const columnLabel = getColumnLabel(column);
  if (validAddresses.length > 1) {
    throw new Error(`Unable to parse email for column '${columnLabel}': found more than one email address`);
  }
  throw new Error(`Unable to parse email for column '${columnLabel}': expected a valid email address`);
};
